import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Agent personas (mirror the backend research swarm roster — multi-pass)
// ---------------------------------------------------------------------------
const RESEARCH_AGENTS = [
  { id: 'coordinator',         name: 'Aria Lansing',     role: 'Research Coordinator',         color: 'bg-indigo-600', text: 'text-indigo-700', phase: 1 },

  { id: 'industry_landscape',  name: 'Dr. Henrik Voss',  role: 'Industry Landscape',           color: 'bg-blue-600',   text: 'text-blue-700',   phase: 2 },
  { id: 'competitive',         name: 'Marcus Chen',      role: 'Competitive Intelligence',     color: 'bg-purple-600', text: 'text-purple-700', phase: 2 },
  { id: 'customer',            name: 'Sofia Ramos',      role: 'Customer & Audience',          color: 'bg-pink-600',   text: 'text-pink-700',   phase: 2, featured: true },
  { id: 'data_statistics',     name: 'Yusuf Khan',       role: 'Data & Statistics',            color: 'bg-emerald-600',text: 'text-emerald-700',phase: 2 },
  { id: 'trends_future',       name: 'Lena Park',        role: 'Trends & Future',              color: 'bg-orange-600', text: 'text-orange-700', phase: 2 },
  { id: 'sources_review',      name: 'James Okonkwo',    role: 'Sources & Credibility',        color: 'bg-teal-600',   text: 'text-teal-700',   phase: 2 },

  { id: 'critic_adversarial',  name: 'Diego Alvarez',    role: 'Devil\'s Advocate',            color: 'bg-red-600',    text: 'text-red-700',    phase: 3, featured: true },
  { id: 'critic_constructive', name: 'Chen Wei',         role: 'Constructive Reviewer',        color: 'bg-cyan-600',   text: 'text-cyan-700',   phase: 3 },

  { id: 'refined_industry_landscape',  name: 'Dr. Henrik Voss', role: 'Refines Industry',     color: 'bg-blue-500',   text: 'text-blue-700',   phase: 4 },
  { id: 'refined_competitive',         name: 'Marcus Chen',     role: 'Refines Competitive', color: 'bg-purple-500', text: 'text-purple-700', phase: 4 },
  { id: 'refined_customer',            name: 'Sofia Ramos',     role: 'Refines Customer',    color: 'bg-pink-500',   text: 'text-pink-700',   phase: 4 },
  { id: 'refined_data_statistics',     name: 'Yusuf Khan',      role: 'Refines Data',         color: 'bg-emerald-500',text: 'text-emerald-700',phase: 4 },
  { id: 'refined_trends_future',       name: 'Lena Park',       role: 'Refines Trends',       color: 'bg-orange-500', text: 'text-orange-700', phase: 4 },
  { id: 'refined_sources_review',      name: 'James Okonkwo',   role: 'Refines Sources',      color: 'bg-teal-500',   text: 'text-teal-700',   phase: 4 },

  { id: 'connector',           name: 'Iris Bernal',      role: 'Cross-Domain Connector',       color: 'bg-amber-600',  text: 'text-amber-700',  phase: 5, featured: true },

  { id: 'synthesis',           name: 'Priya Mehta',      role: 'Synthesis & Insights Lead',    color: 'bg-rose-600',   text: 'text-rose-700',   phase: 6, featured: true },
];

const PHASE_LABELS = {
  1: 'Phase 1 · Plan & Hypotheses',
  2: 'Phase 2 · Initial Specialists (parallel)',
  3: 'Phase 3 · Critique (Adversarial + Constructive, parallel)',
  4: 'Phase 4 · Refinement (parallel)',
  5: 'Phase 5 · Cross-Connection + Targeted Followups',
  6: 'Phase 6 · Confidence-Weighted Synthesis',
};

const initialsOf = (n) => n.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

// ---------------------------------------------------------------------------
// Live progress card (simulated timeline matching real ~210s swarm runtime)
// ---------------------------------------------------------------------------
const TIMELINE = {
  // Phase 1
  coordinator:                   { start: 1,   finish: 12 },
  // Phase 2 (parallel)
  industry_landscape:            { start: 13,  finish: 38 },
  competitive:                   { start: 13,  finish: 38 },
  customer:                      { start: 13,  finish: 38 },
  data_statistics:               { start: 13,  finish: 38 },
  trends_future:                 { start: 13,  finish: 38 },
  sources_review:                { start: 13,  finish: 38 },
  // Phase 3 (parallel)
  critic_adversarial:            { start: 40,  finish: 68 },
  critic_constructive:           { start: 40,  finish: 60 },
  // Phase 4 (parallel)
  refined_industry_landscape:    { start: 70,  finish: 110 },
  refined_competitive:           { start: 70,  finish: 110 },
  refined_customer:              { start: 70,  finish: 110 },
  refined_data_statistics:       { start: 70,  finish: 110 },
  refined_trends_future:         { start: 70,  finish: 110 },
  refined_sources_review:        { start: 70,  finish: 110 },
  // Phase 5
  connector:                     { start: 112, finish: 130 },
  // Phase 6
  synthesis:                     { start: 145, finish: 195 },
};

const statusFor = (id, elapsed) => {
  const t = TIMELINE[id];
  if (!t) return 'queued';
  if (elapsed < t.start) return 'queued';
  if (elapsed < t.finish) return 'working';
  return 'done';
};

const StatusBadge = ({ status }) => {
  if (status === 'done') {
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">Complete</span>;
  }
  if (status === 'working') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700">
        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mr-1.5 animate-pulse" />
        Researching…
      </span>
    );
  }
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500">Queued</span>;
};

const AgentCard = ({ agent, status }) => {
  const isWorking = status === 'working';
  const isDone = status === 'done';
  return (
    <div data-testid={`research-agent-${agent.id}`} className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
      isWorking ? 'bg-orange-50 border-orange-200 shadow-sm ring-1 ring-orange-300'
        : isDone ? 'bg-white border-green-200'
        : 'bg-gray-50 border-gray-200 opacity-70'
    }`}>
      <div className="relative">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs shadow ${agent.color}`}>
          {initialsOf(agent.name)}
        </div>
        {isWorking && <div className="absolute -inset-1 rounded-full border-2 border-orange-400 border-t-transparent animate-spin" />}
        {isDone && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{agent.name}</div>
            <div className={`text-xs font-medium truncate ${agent.text}`}>
              {agent.role}
              {agent.featured && <span className="ml-1.5 text-[9px] uppercase bg-yellow-100 text-yellow-800 px-1 rounded">specialist</span>}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
      </div>
    </div>
  );
};

const SwarmProgress = ({ active, estimatedSec = 200 }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) { setElapsed(0); return; }
    const start = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - start) / 1000), 250);
    return () => clearInterval(id);
  }, [active]);

  const completed = RESEARCH_AGENTS.filter(a => statusFor(a.id, elapsed) === 'done').length;
  const total = RESEARCH_AGENTS.length;
  const pct = Math.min(100, Math.round((completed / total) * 100));
  const left = Math.max(0, Math.ceil(estimatedSec - elapsed));

  // Group agents by phase
  const phases = [1, 2, 3, 4, 5, 6].map(phase => ({
    phase,
    label: PHASE_LABELS[phase],
    agents: RESEARCH_AGENTS.filter(a => a.phase === phase),
  }));

  return (
    <div className="bg-white rounded-xl shadow-lg border-2 border-orange-200 p-6">
      <div className="text-center mb-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200 mb-3">
          <span aria-hidden="true">🔬</span>
          <span className="text-xs font-bold uppercase tracking-wider text-orange-700">Multi-Pass Agentic Research · Live</span>
        </div>
        <h3 className="text-2xl font-bold text-gray-900">Your research team is collaborating</h3>
        <p className="text-sm text-gray-600 mt-1">Plan → Initial pass → Critique → Refine → Connect → Synthesize</p>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs font-semibold whitespace-nowrap">{completed}/{total} agents</div>
        <div className="text-xs text-gray-500 whitespace-nowrap">{left > 0 ? `~${left}s left` : 'finalizing…'}</div>
      </div>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {phases.map(({ phase, label, agents }) => (
          <div key={`phase-${phase}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">{label}</div>
            <div className={agents.length > 1 ? 'grid grid-cols-1 md:grid-cols-2 gap-2' : 'grid gap-2'}>
              {agents.map(a => <AgentCard key={a.id} agent={a} status={statusFor(a.id, elapsed)} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Confidence-tag badge renderer
// Replaces `[FACT, high]` etc. in prose with styled badges
// ---------------------------------------------------------------------------
const TAG_STYLES = {
  FACT:      { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  INFERENCE: { bg: 'bg-amber-100',   text: 'text-amber-800' },
  ESTIMATE:  { bg: 'bg-rose-100',    text: 'text-rose-800' },
};
const CONF_STYLES = {
  high:   { bg: 'bg-green-100',  text: 'text-green-800' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  low:    { bg: 'bg-red-100',    text: 'text-red-800' },
};

const renderInlineTags = (line, lineKey) => {
  const tagRe = /\[(FACT|INFERENCE|ESTIMATE),\s*(high|medium|low)\]/gi;
  const parts = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = tagRe.exec(line)) !== null) {
    if (m.index > last) parts.push(line.slice(last, m.index));
    const kind = m[1].toUpperCase();
    const conf = m[2].toLowerCase();
    const ks = TAG_STYLES[kind];
    const cs = CONF_STYLES[conf];
    parts.push(
      <span
        key={`tag-${lineKey}-${i++}`}
        className={`inline-flex items-center gap-1 align-middle px-1.5 py-0.5 mx-0.5 rounded text-[9px] font-bold uppercase tracking-wider`}
      >
        <span className={`px-1 py-0.5 rounded ${ks.bg} ${ks.text}`}>{kind}</span>
        <span className={`px-1 py-0.5 rounded ${cs.bg} ${cs.text}`}>{conf}</span>
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < line.length) parts.push(line.slice(last));
  return parts;
};

// ---------------------------------------------------------------------------
// Result view
// ---------------------------------------------------------------------------
const ResearchResult = ({ project, onReset }) => {
  const trace = project.swarm_trace || {};
  const cost = trace.estimated_cost_usd ?? 0;
  const reportText = project.report || '';
  const conf = project.confidence_summary || {};
  const hypotheses = project.hypothesis_outcomes || [];

  const renderReport = (txt) => {
    return txt.split('\n').map((line, i) => {
      if (/^\*\*[^*]+\*\*/.test(line)) {
        return (
          <h4 key={`h-${i}`} className="text-base font-bold text-gray-900 mt-5 mb-2">
            {line.replace(/\*\*/g, '')}
          </h4>
        );
      }
      if (line.trim()) return (
        <p key={`p-${i}`} className="text-sm text-gray-700 leading-relaxed mb-2">
          {renderInlineTags(line, i)}
        </p>
      );
      return null;
    });
  };

  return (
    <div className="space-y-6" data-testid="research-result">
      {/* Headline + meta */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-orange-600">Headline Takeaway</span>
              <span className="text-xs text-gray-400">·</span>
              <span className="text-xs text-gray-500">{(project.tags || []).slice(0, 3).join(' · ')}</span>
            </div>
            <p className="text-lg font-bold text-gray-900 leading-snug" data-testid="research-headline">
              {project.headline || 'Headline unavailable'}
            </p>
          </div>
          <button
            data-testid="research-new-btn"
            onClick={onReset}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600"
          >
            New research
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-gray-200 text-center">
          <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Duration</div><div className="text-sm font-bold">{trace.total_duration_sec?.toFixed(1) ?? '—'}s</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Tokens (in)</div><div className="text-sm font-bold">{trace.total_input_tokens?.toLocaleString() ?? '—'}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Tokens (out)</div><div className="text-sm font-bold">{trace.total_output_tokens?.toLocaleString() ?? '—'}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Estimated cost</div><div className="text-sm font-bold text-green-700">${cost.toFixed(4)}</div></div>
        </div>
        <p className="text-[11px] text-gray-500 mt-3 text-center">
          Cost is an estimate based on public Anthropic pricing × token usage. Actual Emergent LLM credit usage may differ.
        </p>
      </div>

      {/* Report body */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Research Report</h3>
        <p className="text-xs text-gray-500 mb-4">{project.brief.slice(0, 200)}{project.brief.length > 200 ? '…' : ''}</p>
        <div data-testid="research-report-body">{renderReport(reportText)}</div>
      </div>

      {/* Confidence audit */}
      {(conf.high_confidence_claims?.length > 0 || conf.medium_confidence_claims?.length > 0 || conf.low_confidence_claims?.length > 0) && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6" data-testid="confidence-audit">
          <h3 className="text-lg font-bold text-gray-900 mb-1">Confidence Audit</h3>
          <p className="text-xs text-gray-500 mb-4">
            Every meaningful claim above is tagged with its evidence level. Use this list as a quick QA pass.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-[10px] font-bold uppercase">High</span>
                <span className="text-xs text-gray-600">{(conf.high_confidence_claims || []).length} claims</span>
              </div>
              <ul className="space-y-1.5">
                {(conf.high_confidence_claims || []).map((c, i) => (
                  <li key={`hi-${i}`} className="text-[12px] text-gray-700 leading-snug">{renderInlineTags(c, `hi-${i}`)}</li>
                ))}
              </ul>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 text-[10px] font-bold uppercase">Medium</span>
                <span className="text-xs text-gray-600">{(conf.medium_confidence_claims || []).length} claims</span>
              </div>
              <ul className="space-y-1.5">
                {(conf.medium_confidence_claims || []).map((c, i) => (
                  <li key={`me-${i}`} className="text-[12px] text-gray-700 leading-snug">{renderInlineTags(c, `me-${i}`)}</li>
                ))}
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-bold uppercase">Low</span>
                <span className="text-xs text-gray-600">{(conf.low_confidence_claims || []).length} claims</span>
              </div>
              <ul className="space-y-1.5">
                {(conf.low_confidence_claims || []).map((c, i) => (
                  <li key={`lo-${i}`} className="text-[12px] text-gray-700 leading-snug">{renderInlineTags(c, `lo-${i}`)}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Hypothesis outcomes */}
      {hypotheses.length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6" data-testid="hypothesis-outcomes">
          <h3 className="text-lg font-bold text-gray-900 mb-1">Hypothesis Outcomes</h3>
          <p className="text-xs text-gray-500 mb-4">
            The Coordinator stated falsifiable hypotheses up front. Here's how the research scored each one.
          </p>
          <div className="space-y-2">
            {hypotheses.map((h, i) => {
              const verdict = (h.verdict || 'unresolved').toLowerCase();
              const vStyle = verdict === 'supported' ? 'bg-green-100 text-green-800'
                : verdict === 'refuted' ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-700';
              return (
                <div key={`hyp-${i}`} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase whitespace-nowrap ${vStyle}`}>{verdict}</span>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 leading-snug">{h.hypothesis}</div>
                    {h.evidence && <div className="text-xs text-gray-600 mt-1">{h.evidence}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-agent breakdown */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Agent Contributions</h3>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {(trace.agents || []).map((a, idx) => {
            // Try direct match first
            let meta = RESEARCH_AGENTS.find(x => x.id === a.agent);
            // Handle dynamic followup_<key> agents (not in static roster)
            if (!meta && a.agent?.startsWith('followup_')) {
              const baseKey = a.agent.replace('followup_', '');
              const base = RESEARCH_AGENTS.find(x => x.id === baseKey);
              if (base) meta = { ...base, role: `Targeted Followup · ${base.role}` };
            }
            if (!meta) {
              meta = { name: a.agent, role: a.agent, color: 'bg-gray-500', text: 'text-gray-700' };
            }
            return (
              <div key={`row-${a.agent}-${idx}`} className={`flex items-center gap-3 p-2.5 rounded-lg border ${a.success ? 'bg-white border-gray-200' : 'bg-rose-50 border-rose-200'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[11px] ${meta.color}`}>
                  {initialsOf(meta.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-bold truncate ${meta.text}`}>{meta.role}</div>
                  <div className="text-[11px] text-gray-500 truncate">{meta.name} · {a.model.split('/').pop()} · {a.duration_sec?.toFixed(1)}s</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-mono text-gray-700">${a.cost_usd?.toFixed(4) ?? '—'}</div>
                  <div className="text-[10px] text-gray-500">in {a.input_tokens} · out {a.output_tokens}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sub-questions */}
      {(project.subquestions || []).length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Research Questions Investigated</h3>
          <ol className="list-decimal list-inside space-y-2">
            {project.subquestions.map((q, i) => (
              <li key={`sq-${i}-${q.slice(0, 12)}`} className="text-sm text-gray-700 leading-relaxed">{q}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AgenticResearch = ({ user }) => {
  const [brief, setBrief] = useState('');
  const [objectives, setObjectives] = useState('');
  const [audience, setAudience] = useState('');
  const [constraints, setConstraints] = useState('');
  const [provider, setProvider] = useState('claude');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [projects, setProjects] = useState([]);

  const allowed = user && (user.is_admin || user.agentic_research_enabled);

  const loadProjects = useCallback(async () => {
    if (!allowed) return;
    try {
      const r = await axios.get(`${API}/agentic-research`, { withCredentials: true });
      setProjects(r.data.projects || []);
    } catch (err) {
      logger.error('Error loading research projects:', err);
    }
  }, [allowed]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const submit = async () => {
    if (brief.trim().length < 20) {
      setError('Brief must be at least 20 characters.');
      return;
    }
    setError(null);
    setRunning(true);
    setResult(null);
    try {
      // 1. Kick off the research run (returns immediately with project_id)
      const startResp = await axios.post(
        `${API}/agentic-research`,
        { brief: brief.trim(), objectives, target_audience: audience, constraints, provider },
        { withCredentials: true, timeout: 30_000 },
      );
      const projectId = startResp.data.id;
      logger.info('Research project started:', projectId);
      
      // 2. Poll until complete | failed (up to 12 minutes for the multi-pass pipeline)
      const maxAttempts = 144; // 144 × 5s = 720s = 12 minutes
      let finalProject = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const pollResp = await axios.get(
            `${API}/agentic-research/${projectId}`,
            { withCredentials: true, timeout: 30_000 },
          );
          const status = pollResp.data.status;
          if (status === 'complete') {
            finalProject = pollResp.data;
            break;
          }
          if (status === 'failed') {
            throw new Error(pollResp.data.error || 'Research run failed.');
          }
          // status === 'running' — keep polling
        } catch (pollErr) {
          // Single poll failure shouldn't kill the loop; only abort on backend "failed"
          if (pollErr.message?.includes('Research run failed')) throw pollErr;
          logger.warn('Poll attempt failed (transient):', pollErr.message);
        }
      }
      
      if (!finalProject) {
        throw new Error('Research is taking longer than expected. Check the history list in a moment.');
      }
      setResult(finalProject);
      loadProjects();
    } catch (err) {
      logger.error('Research submit error:', err);
      setError(err.response?.data?.detail || err.message || 'Research failed.');
    } finally {
      setRunning(false);
    }
  };

  const openProject = async (id) => {
    try {
      const r = await axios.get(`${API}/agentic-research/${id}`, { withCredentials: true });
      setResult(r.data);
    } catch (err) {
      logger.error('Error loading project:', err);
    }
  };

  // ----- Access denied view -----
  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md border border-gray-200 p-8" data-testid="research-locked">
        <div className="text-center">
          <span className="text-4xl">🔒</span>
          <h2 className="text-xl font-bold text-gray-900 mt-3">Agentic Research is restricted</h2>
          <p className="text-sm text-gray-600 mt-2">
            This feature is currently available to admins and team members granted access.
            Each research run consumes LLM credits, so we're rolling it out gradually.
          </p>
          <p className="text-sm text-gray-700 mt-4">
            Ask an admin to grant you access via the Admin Panel.
          </p>
        </div>
      </div>
    );
  }

  // ----- Active result -----
  if (result) {
    return <ResearchResult project={result} onReset={() => setResult(null)} />;
  }

  // ----- Running -----
  if (running) {
    return (
      <div className="max-w-3xl mx-auto">
        <SwarmProgress active={running} estimatedSec={provider === 'claude' ? 360 : 200} />
        <p className="text-center text-xs text-gray-500 mt-4">
          {provider === 'claude'
            ? 'Powered by Claude Sonnet 4 · Multi-pass agentic pipeline · Typical run 5-7 min · ~$0.30-0.60'
            : 'Powered by Kimi K2 (Moonshot) · Multi-pass agentic pipeline · Typical run 3-5 min · ~$0.15-0.25'}
        </p>
      </div>
    );
  }

  // ----- Brief intake form -----
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="bg-gradient-to-br from-orange-50 via-white to-pink-50 rounded-xl border border-orange-200 p-6">
        <div className="flex items-start gap-3">
          <span className="text-3xl">🔬</span>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Multi-Pass Agentic Research</h2>
            <p className="text-sm text-gray-700 mt-1">
              A 20-agent specialist team — initial research, adversarial critique, constructive review,
              refinement, cross-domain connection, and confidence-weighted synthesis — delivers a
              citable BCM-style report with a transparent confidence audit.
            </p>
            <p className="text-xs text-orange-700 mt-2 font-semibold">
              Claude Sonnet 4: ~$0.30-0.60 / run · ~5-7 min · Kimi K2: ~$0.15-0.25 / run · ~3-5 min
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 space-y-4">
        {/* Provider toggle */}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-gray-700">LLM Provider</label>
          <div className="mt-1 grid grid-cols-2 gap-2" data-testid="provider-toggle">
            <button
              type="button"
              data-testid="provider-claude"
              onClick={() => setProvider('claude')}
              className={`p-3 rounded-lg border-2 text-left transition ${
                provider === 'claude'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-bold text-gray-900">Claude Sonnet 4</div>
              <div className="text-[11px] text-gray-600 mt-0.5">~$0.30-0.60 · ~5-7min · default · highest quality</div>
            </button>
            <button
              type="button"
              data-testid="provider-kimi"
              onClick={() => setProvider('kimi')}
              className={`p-3 rounded-lg border-2 text-left transition ${
                provider === 'kimi'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-bold text-gray-900">Kimi K2 <span className="text-[10px] text-purple-600 ml-1">BETA</span></div>
              <div className="text-[11px] text-gray-600 mt-0.5">~$0.15-0.25 · ~3-5min · Moonshot direct · faster</div>
            </button>
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Research brief <span className="text-red-500">*</span></label>
          <textarea
            data-testid="research-brief-input"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="e.g. Research the U.S. premium pet food market for our retail client. Focus on dog food. We need to understand the players, consumer trends, and where the white space is."
            rows={4}
            className="mt-1 w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
          />
          <p className="text-[11px] text-gray-500 mt-1">Be specific. Mention the industry, geography, audience, and what decision the research informs.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Objective</label>
            <input
              data-testid="research-objective-input"
              value={objectives}
              onChange={(e) => setObjectives(e.target.value)}
              placeholder="What decision will this inform?"
              className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Audience</label>
            <input
              data-testid="research-audience-input"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="Who reads this?"
              className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Constraints (optional)</label>
          <input
            data-testid="research-constraints-input"
            value={constraints}
            onChange={(e) => setConstraints(e.target.value)}
            placeholder="e.g. Geography limited to North America; no primary research"
            className="mt-1 w-full p-2.5 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

        <button
          data-testid="research-submit-btn"
          onClick={submit}
          disabled={brief.trim().length < 20}
          className="w-full py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition"
        >
          Launch the Research Team
        </button>
      </div>

      {projects.length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Recent Research</h3>
          <div className="space-y-2">
            {projects.slice(0, 10).map((p) => (
              <button
                key={p.id}
                data-testid={`research-history-${p.id}`}
                onClick={() => openProject(p.id)}
                className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition"
              >
                <div className="text-sm font-bold text-gray-900 line-clamp-1">{p.headline || p.brief.slice(0, 80)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {new Date(p.created_at).toLocaleString()} ·
                  {p.swarm_trace?.total_duration_sec ? ` ${p.swarm_trace.total_duration_sec.toFixed(0)}s ·` : ''}
                  {p.swarm_trace?.estimated_cost_usd ? ` $${p.swarm_trace.estimated_cost_usd.toFixed(4)}` : ''}
                  {p.tags && p.tags.length > 0 && ` · ${p.tags.slice(0, 3).join(', ')}`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgenticResearch;
